export type ApiErrorInfo = {
  status: number;
  statusText: string;
  url?: string;
  method?: string;
  contentType?: string | null;
  rawBody?: string;
  message: string;
};

const HTML_RE = /<(!doctype|html|head|body)\b/i;

function isProbablyHtml(text: string) {
  return HTML_RE.test(text);
}

function stripHtml(text: string) {
  return text
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/<\/?[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function firstNonEmptyLine(text: string) {
  return (
    text
      .split(/\r?\n/)
      .map((l) => l.trim())
      .find(Boolean) ?? ""
  );
}

function statusToFriendly(status: number): string | null {
  switch (status) {
    case 400:
      return "Ugyldig forespørsel (400).";
    case 401:
      return "Du er ikke logget inn eller har ikke tilgang (401).";
    case 403:
      return "Du har ikke tilgang til dette (403).";
    case 404:
      return "Fant ikke ressursen (404).";
    case 409:
      return "Konflikt: endringen kolliderer med eksisterende data (409).";
    case 413:
      return "Forespørselen er for stor (413).";
    case 429:
      return "For mange forespørsler – prøv igjen om litt (429).";
    case 500:
      return "Serverfeil (500).";
    case 502:
    case 503:
    case 504:
      return "Tjenesten er midlertidig utilgjengelig. Prøv igjen om litt.";
    default:
      return null;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function tryExtractJsonMessage(raw: string): string | null {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed)) return null;

    const message = parsed.message;
    if (typeof message === "string" && message.trim()) return message.trim();

    const error = parsed.error;
    if (typeof error === "string" && error.trim()) return error.trim();

    const title = parsed.title;
    if (typeof title === "string" && title.trim()) return title.trim();

    const detail = parsed.detail;
    if (typeof detail === "string" && detail.trim()) return detail.trim();

    const errors = parsed.errors;
    if (isRecord(errors)) {
      const combined = Object.values(errors)
        .flatMap((v) => (Array.isArray(v) ? v : [v]))
        .filter((v): v is string => typeof v === "string" && v.trim().length > 0)
        .join(", ");

      if (combined) return combined;
    }

    return null;
  } catch {
    return null;
  }
}


export async function buildApiErrorInfo(
  response: Response,
  opts?: { method?: string; fallbackMessage?: string },
): Promise<ApiErrorInfo> {
  const contentType = response.headers.get("content-type");
  const status = response.status;
  const statusText = response.statusText;

  const fallback =
    opts?.fallbackMessage ??
    `API-feil (${status}${statusText ? ` ${statusText}` : ""}).`;

  let raw: string;
  try {
    raw = await response.text();
  } catch {
    raw = "";
  }

  const jsonMessage =
    (contentType?.includes("application/json") ? tryExtractJsonMessage(raw) : null) ??
    (raw.trim().startsWith("{") ? tryExtractJsonMessage(raw) : null);

  let bodySummary = "";
  if (raw) {
    if (isProbablyHtml(raw)) {
      const stripped = stripHtml(raw);
      bodySummary = firstNonEmptyLine(stripped);
    } else {
      bodySummary = firstNonEmptyLine(raw);
    }
  }

  const friendly = statusToFriendly(status);
  const message =
    friendly ??
    jsonMessage ??
    (bodySummary ? `${fallback} ${bodySummary}` : fallback);

  return {
    status,
    statusText,
    url: response.url,
    method: opts?.method,
    contentType,
    rawBody: raw,
    message,
  };
}

export class ApiError extends Error {
  info: ApiErrorInfo;

  constructor(info: ApiErrorInfo) {
    super(info.message);
    this.name = "ApiError";
    this.info = info;
  }
}

export type UiErrorVariant = "error" | "warning" | "info";

export type UiErrorModel = {
  variant: UiErrorVariant;
  title?: string;
  message: string;
  action?: {
    label: string;
    href?: string;
    onClick?: () => void;
  };
};

export function toUiErrorModel(err: unknown): UiErrorModel {
  const fallback: UiErrorModel = {
    variant: "error",
    message: "Noe gikk galt. Prøv igjen.",
  };

  if (!(err instanceof Error)) return fallback;

  if (err.name !== "ApiError") {
    return {
      variant: "error",
      message: err.message || fallback.message,
    };
  }

  const apiErr = err as unknown as { info?: { status?: number; message?: string } };
  const status = apiErr.info?.status;
  const message = apiErr.info?.message || err.message || fallback.message;

  switch (status) {
    case 401:
      return {
        variant: "info",
        title: "Innlogging kreves",
        message:
          "Du er ikke logget inn (eller sesjonen din har utløpt). Logg inn og prøv igjen.",
        action: {
          label: "Gå til innlogging",
          href: "/util/login",
        },
      };

    case 403:
      return {
        variant: "warning",
        title: "Ingen tilgang",
        message: "Du har ikke tilgang til denne funksjonen.",
      };

    case 404:
      return {
        variant: "warning",
        title: "Fant ikke",
        message: "Ressursen finnes ikke (404).",
      };

    default:
      return {
        variant: "error",
        message,
      };
  }
}
